import { ClassAttributes, FC, HTMLAttributes, ReactNode } from "react"
import { api, definition, styles, context, wire } from "@uesio/ui"
import ReactMarkdown, { Components, ExtraProps } from "react-markdown"
import remarkGfm from "remark-gfm"
import CodeField from "../codefield/codefield"
import rehypeShikiFromHighlighter from "@shikijs/rehype/core"
import {
  highlighter,
  type HighlightTheme,
  highlightThemeDefault,
} from "../syntax-highlight"
import { visit } from "unist-util-visit"
import type { Element as HastElement, Root } from "hast"

interface MarkDownFieldProps {
  setValue?: (value: wire.FieldValue) => void
  value: wire.FieldValue
  resolveMerges?: boolean
  mode?: context.FieldMode
  readonly?: boolean
  theme?: HighlightTheme
  components?: Components
}

type HeadingElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"

const extractTextFromHast = (
  node?: HastElement | Root | { value?: string; children?: unknown[] },
): string => {
  if (!node) return ""
  if ("value" in node && typeof node.value === "string") {
    return node.value
  }
  if ("children" in node && Array.isArray(node.children)) {
    return node.children
      .map((child) =>
        extractTextFromHast(
          child as
            HastElement | Root | { value?: string; children?: unknown[] },
        ),
      )
      .join("")
  }
  return ""
}

const generateSlug = (content: ReactNode) => {
  if (!(typeof content === "string")) return
  return content
    .replace(/^\s+|\s+$/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
}

const Heading: FC<
  ClassAttributes<HTMLHeadingElement> &
    HTMLAttributes<HTMLHeadingElement> &
    ExtraProps
> = ({ node, className, children }) => {
  const Element = (node?.tagName || "h1") as HeadingElement
  return (
    <Element id={generateSlug(children)} className={className}>
      {children}
    </Element>
  )
}

/**
 * Rehype plugin to add an 'inline' property to <code> elements
 * Sets 'inline' property to true if the <code> is not within a <pre> tag
 */
const rehypeCodeMetadata = () => {
  return (tree: Root): undefined => {
    visit(tree, "element", (node: HastElement, _index, parent: HastElement) => {
      if (node.tagName !== "code") return

      const classNames = Array.isArray(node.properties.className)
        ? node.properties.className
        : []
      const languageClass = classNames.find(
        (value): value is string =>
          typeof value === "string" && value.startsWith("language-"),
      )

      node.properties.dataRaw = extractTextFromHast(node)
      if (languageClass) {
        node.properties.dataLanguage = languageClass.replace("language-", "")
      }

      if (parent.tagName !== "pre") {
        node.properties.inline = true
      }
    })
  }
}

const StyleDefaults = Object.freeze({
  root: [],
  h1: [],
  h2: [],
  h3: [],
  h4: [],
  h5: [],
  h6: [],
  p: [],
  ol: [],
  ul: [],
  li: [],
  code: [],
  codeInline: [],
  a: [],
  img: [],
  imgWrapper: [],
  imgTitle: [],
  imgInner: [],
})

const isRelativeUrl = (url?: string) => (url ? url?.startsWith("./") : false)

const MarkDownField: definition.UtilityComponent<MarkDownFieldProps> = (
  props,
) => {
  const {
    context,
    components,
    mode,
    readonly,
    setValue,
    theme = highlightThemeDefault,
  } = props

  const classes = styles.useUtilityStyleTokens(
    StyleDefaults,
    props,
    "uesio/io.markdownfield",
  )
  const value =
    props.resolveMerges === false
      ? (props.value as string)
      : context.mergeString(props.value as string)
  const record = context.getRecord()
  const recordid = record?.getIdFieldValue()
  const recordmod =
    (record?.getFieldValue<number>("uesio/core.updatedat") || 0) + ""
  const isEditMode = !readonly && mode === "EDIT" && setValue

  if (isEditMode) {
    return (
      // TODO: Since MarkdownField defers to CodeField in EDIT mode, it needs to support the same set of props as CodeField
      // that are appropriate for markdown (e.g., debounce, options, onMount, beforeMount, etc.)
      <CodeField
        language="markdown"
        className={classes.root}
        value={value}
        context={context}
        setValue={(v) => setValue(v)}
        mode="EDIT"
        theme={theme}
      />
    )
  }

  const markdownComponents: Components = {
    p: (props) => <p className={classes.p}>{props.children}</p>,
    h1: (props) => <Heading {...props} className={classes.h1} />,
    h2: (props) => <Heading {...props} className={classes.h2} />,
    h3: (props) => <Heading {...props} className={classes.h3} />,
    h4: (props) => <Heading {...props} className={classes.h4} />,
    h5: (props) => <Heading {...props} className={classes.h5} />,
    h6: (props) => <Heading {...props} className={classes.h6} />,
    ol: (props) => <ol className={classes.ol}>{props.children}</ol>,
    ul: (props) => <ul className={classes.ul}>{props.children}</ul>,
    li: (props) => <li className={classes.li}>{props.children}</li>,
    img: (props) => {
      let { src, alt } = props
      const { title } = props

      const metastring = alt
      alt = metastring?.replace(/ *\{[^)]*\} */g, "")
      const metaWidth = metastring?.match(/{([^}]+)x/)
      const metaHeight = metastring?.match(/x([^}]+)}/)
      const width = metaWidth ? metaWidth[1] : undefined
      const height = metaHeight ? metaHeight[1] : undefined

      if (src && isRelativeUrl(src)) {
        if (recordid) {
          src = api.file.getAttachmentURL(
            context,
            recordid,
            src.slice(2),
            recordmod + "",
          )
        } else {
          src = ""
        }
      }

      return (
        <div className={classes.imgWrapper}>
          <div className={classes.imgInner}>
            <img
              className={classes.img}
              alt={alt}
              title={title}
              src={src}
              width={width}
              height={height}
            />
          </div>
          <div className={classes.imgTitle}>{title}</div>
        </div>
      )
    },
    a: (props) => (
      <a className={classes.a} href={props.href}>
        {props.children}
      </a>
    ),
    code: ({ node, className, children, ...props }) => {
      const cn = node?.properties.inline ? classes.codeInline : classes.code
      return (
        <code className={cn} {...props}>
          {children}
        </code>
      )
    },
    ...components,
  }

  return (
    <div className={classes.root}>
      <ReactMarkdown
        children={value}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          [
            rehypeShikiFromHighlighter,
            highlighter,
            { theme: theme, inline: "tailing-curly-colon" },
          ],
          rehypeCodeMetadata,
        ]}
        components={markdownComponents}
      />
    </div>
  )
}

export default MarkDownField
